/**
 * Signup Pricing Page
 * 
 * Simple page shown when:
 * - User tries to login via OAuth but has no account
 * - User needs to select a plan before creating an account
 * 
 * Flow:
 * 1. Show banner explaining they need to select a plan
 * 2. Show pricing grid
 * 3. When they select a plan, redirect to /signup with plan in navigation state
 */
import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Sparkles, LogIn, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSafeMode } from '../contexts/SafeModeContext';
import PricingSection from '../components/pricing/PricingSection';

const SignupPricingPage = () => {
  const navigate = useNavigate();
  const { safeMode } = useSafeMode();

  // Handle plan selection - redirect to signup with plan in state
  const handlePlanSelect = (plan) => {
    navigate('/signup', {
      state: {
        selectedPlanId: plan.plan_id,
        planLabel: plan.label
      }
    });
  };

  return (
    <div className={`min-h-screen ${safeMode ? 'bg-gray-50' : 'bg-slate-950'}`}>
      {/* Header */}
      <header className={`py-6 px-6 border-b ${
        safeMode ? 'bg-white border-gray-200' : 'bg-slate-900 border-gray-800'
      }`}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              safeMode ? 'bg-blue-600' : 'bg-red-600'
            }`}>
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className={`text-xl font-bold ${safeMode ? 'text-gray-900' : 'text-white'}`}>
              StackRanked
            </span>
          </Link>
          
          <div className="flex items-center gap-3">
            <span className={`text-sm ${safeMode ? 'text-gray-600' : 'text-gray-400'}`}>
              Already have an account?
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/login')}
              className={safeMode ? '' : 'border-gray-700 text-gray-300 hover:bg-gray-800'}
            >
              <LogIn className="w-4 h-4 mr-2" />
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Banner - Explain why they're here */}
      <div className={`py-6 px-6 ${
        safeMode 
          ? 'bg-blue-50 border-b border-blue-200' 
          : 'bg-amber-900/20 border-b border-amber-500/30'
      }`}>
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <AlertCircle className={`w-6 h-6 flex-shrink-0 ${
            safeMode ? 'text-blue-600' : 'text-amber-500'
          }`} />
          <div>
            <h2 className={`font-semibold ${
              safeMode ? 'text-blue-900' : 'text-amber-200'
            }`}>
              Select a Plan to Create Your Account
            </h2>
            <p className={`text-sm ${
              safeMode ? 'text-blue-700' : 'text-amber-300/80'
            }`}>
              We couldn't find an account with your email. Please select a plan below to get started.
            </p>
          </div>
        </div>
      </div>

      {/* Pricing Grid */}
      <div className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className={`text-3xl font-bold ${
              safeMode ? 'text-gray-900' : 'text-white'
            }`}>
              Choose Your Plan
            </h1>
            <p className={`mt-2 ${
              safeMode ? 'text-gray-600' : 'text-gray-400'
            }`}>
              Select a plan to create your account and start your trial
            </p>
          </div>
          
          <PricingSection 
            safeMode={safeMode} 
            onPlanSelect={handlePlanSelect}
          />
        </div>
      </div>
    </div>
  );
};

export default SignupPricingPage;
